import { useState, useEffect, useCallback, useRef } from 'react';
import { Send, Users, Search, CheckSquare, Square, Image, Type, Mail, MessageSquare, Phone, Loader2, X, Upload } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import api from '@/lib/api';
import { toast } from 'sonner';

const RECIPIENT_TYPES = [
  { value: 'patient', label: 'Patients', icon: Users },
  { value: 'referring_doctor', label: 'Referring Doctors', icon: Users },
  { value: 'community_worker', label: 'Community Workers', icon: Users }
];

const CHANNELS = [
  { value: 'whatsapp', label: 'WhatsApp', icon: MessageSquare, color: 'text-green-600' },
  { value: 'sms', label: 'SMS', icon: Phone, color: 'text-blue-600' },
  { value: 'email', label: 'Email', icon: Mail, color: 'text-purple-600' }
];

export default function SendMessage() {
  const [recipientType, setRecipientType] = useState('patient');
  const [recipients, setRecipients] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  
  // Message options
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [useCustomMessage, setUseCustomMessage] = useState(false);
  const [selectedChannels, setSelectedChannels] = useState(['whatsapp']);
  const [emailSubject, setEmailSubject] = useState('');
  
  // Branding options
  const [includeBranding, setIncludeBranding] = useState(false);
  const [logoUrl, setLogoUrl] = useState('');
  const [tagline, setTagline] = useState('');
  const [bannerUrl, setBannerUrl] = useState('');
  const [hospitalBranding, setHospitalBranding] = useState({});
  
  // File upload state
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const logoInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  
  // Preview dialog
  const [showPreview, setShowPreview] = useState(false);
  const [previewMessage, setPreviewMessage] = useState('');

  // Fetch recipients when type changes
  useEffect(() => {
    fetchRecipients();
    fetchTemplates();
    fetchHospitalBranding();
  }, [recipientType]);

  const fetchRecipients = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/messaging/recipients?recipient_type=${recipientType}`);
      setRecipients(response.data.recipients || []);
      setSelectedRecipients([]);
    } catch (error) {
      console.error('Failed to fetch recipients:', error);
      toast.error('Failed to fetch recipients');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/message-templates');
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchHospitalBranding = async () => {
    try {
      const response = await api.get('/messaging/hospital-branding');
      setHospitalBranding(response.data || {});
      if (response.data?.logo_url) {
        setLogoUrl(response.data.logo_url);
        setLogoPreview(response.data.logo_url);
      }
      if (response.data?.tagline) setTagline(response.data.tagline);
    } catch (error) {
      console.error('Failed to fetch branding:', error);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    
    // Upload to server
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/files/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setLogoUrl(response.data.url);
      toast.success('Logo uploaded successfully');
    } catch (error) {
      console.error('Failed to upload logo:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    
    setBannerFile(file);
    setBannerPreview(URL.createObjectURL(file));
    
    // Upload to server
    setUploadingBanner(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/files/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setBannerUrl(response.data.url);
      toast.success('Banner uploaded successfully');
    } catch (error) {
      console.error('Failed to upload banner:', error);
      toast.error('Failed to upload banner');
    } finally {
      setUploadingBanner(false);
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview('');
    setLogoUrl('');
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const removeBanner = () => {
    setBannerFile(null);
    setBannerPreview('');
    setBannerUrl('');
    if (bannerInputRef.current) bannerInputRef.current.value = '';
  };

  const filteredRecipients = recipients.filter(r => 
    r.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.phone?.includes(searchTerm) ||
    r.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleRecipient = (recipient) => {
    setSelectedRecipients(prev => {
      const exists = prev.find(r => r.id === recipient.id);
      if (exists) {
        return prev.filter(r => r.id !== recipient.id);
      }
      return [...prev, recipient];
    });
  };

  const selectAll = () => {
    if (selectedRecipients.length === filteredRecipients.length) {
      setSelectedRecipients([]);
    } else {
      setSelectedRecipients([...filteredRecipients]);
    }
  };

  const toggleChannel = (channel) => {
    setSelectedChannels(prev => {
      if (prev.includes(channel)) {
        return prev.filter(c => c !== channel);
      }
      return [...prev, channel];
    });
  };

  const getMessageContent = useCallback(() => {
    if (useCustomMessage) {
      return customMessage;
    }
    const template = templates.find(t => t.id === selectedTemplate);
    return template?.content || '';
  }, [useCustomMessage, customMessage, selectedTemplate, templates]);

  const generatePreview = () => {
    let message = getMessageContent();
    // Replace sample placeholders for preview
    message = message.replace(/\{\{Recipient_Name\}\}/g, 'John Doe');
    message = message.replace(/\{\{Doctor_Name\}\}/g, 'Dr. Smith');
    message = message.replace(/\{\{Patient_Name\}\}/g, 'John Doe');
    message = message.replace(/\{\{Hospital_Name\}\}/g, hospitalBranding.name || 'Hospital');
    message = message.replace(/\{\{Date\}\}/g, new Date().toLocaleDateString());
    setPreviewMessage(message);
    setShowPreview(true);
  };

  const handleSend = async () => {
    if (selectedRecipients.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }
    
    if (selectedChannels.length === 0) {
      toast.error('Please select at least one channel');
      return;
    }
    
    if (!useCustomMessage && !selectedTemplate) {
      toast.error('Please select a template or enter a custom message');
      return;
    }
    
    if (useCustomMessage && !customMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    if (selectedChannels.includes('email') && !emailSubject.trim()) {
      toast.error('Please enter an email subject');
      return;
    }

    setSending(true);
    try {
      const payload = {
        recipients: selectedRecipients.map(r => ({
          id: r.id,
          name: r.name,
          phone: r.phone,
          email: r.email,
          recipient_type: recipientType
        })),
        template_id: useCustomMessage ? null : selectedTemplate,
        custom_message: useCustomMessage ? customMessage : null,
        channels: selectedChannels,
        subject: emailSubject || null,
        branding: includeBranding ? {
          include_logo: true,
          logo_url: logoUrl || null,
          tagline: tagline || null,
          banner_image_url: bannerUrl || null
        } : null
      };

      const response = await api.post('/messaging/send-bulk', payload);
      
      toast.success(
        `Messages sent! ${response.data.successful} successful, ${response.data.failed} failed`
      );
      
      // Reset selections
      setSelectedRecipients([]);
      
    } catch (error) {
      console.error('Failed to send messages:', error);
      toast.error(error.response?.data?.detail || 'Failed to send messages');
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="send-message-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Send Message</h1>
          <p className="text-slate-600 mt-1">Send bulk messages via WhatsApp, SMS, or Email</p>
        </div>
        <div className="flex items-center gap-4">
          {selectedRecipients.length > 0 && (
            <Badge className="bg-[#4A238F] text-white px-3 py-1">
              {selectedRecipients.length} selected
            </Badge>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel - Recipients */}
        <div className="lg:col-span-2 space-y-4">
          {/* Recipient Type Tabs */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Select Recipients</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs value={recipientType} onValueChange={setRecipientType}>
                <TabsList className="grid grid-cols-3 mb-4 bg-slate-200">
                  {RECIPIENT_TYPES.map(type => (
                    <TabsTrigger 
                      key={type.value} 
                      value={type.value} 
                      className="text-sm text-slate-700 data-[state=active]:bg-[#4A238F] data-[state=active]:text-white"
                    >
                      {type.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>

              {/* Search and Select All */}
              <div className="flex items-center gap-4 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search by name, phone, or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Button variant="outline" size="sm" onClick={selectAll}>
                  {selectedRecipients.length === filteredRecipients.length && filteredRecipients.length > 0
                    ? 'Deselect All'
                    : 'Select All'}
                </Button>
              </div>

              {/* Recipients List */}
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-[#4A238F]" />
                </div>
              ) : filteredRecipients.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  No recipients found
                </div>
              ) : (
                <div className="max-h-[400px] overflow-y-auto space-y-2 pr-2">
                  {filteredRecipients.map(recipient => {
                    const isSelected = selectedRecipients.some(r => r.id === recipient.id);
                    return (
                      <div
                        key={recipient.id}
                        onClick={() => toggleRecipient(recipient)}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          isSelected 
                            ? 'bg-purple-50 border-[#4A238F]' 
                            : 'bg-white border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <Checkbox checked={isSelected} />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 truncate">{recipient.name}</p>
                          <div className="flex items-center gap-3 text-sm text-slate-500">
                            {recipient.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {recipient.phone}
                              </span>
                            )}
                            {recipient.email && (
                              <span className="flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                {recipient.email}
                              </span>
                            )}
                          </div>
                        </div>
                        {recipient.specialty && (
                          <Badge variant="outline" className="text-xs">{recipient.specialty}</Badge>
                        )}
                        {recipient.area && (
                          <Badge variant="outline" className="text-xs">{recipient.area}</Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="mt-4 pt-4 border-t text-sm text-slate-500">
                Showing {filteredRecipients.length} of {recipients.length} recipients
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Panel - Message Options */}
        <div className="space-y-4">
          {/* Channel Selection */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Select Channels</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {CHANNELS.map(channel => (
                <div
                  key={channel.value}
                  onClick={() => toggleChannel(channel.value)}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedChannels.includes(channel.value)
                      ? 'bg-purple-50 border-[#4A238F]'
                      : 'bg-white border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <Checkbox checked={selectedChannels.includes(channel.value)} />
                  <channel.icon className={`h-5 w-5 ${channel.color}`} />
                  <span className="font-medium">{channel.label}</span>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Message Content */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Message Content</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Template or Custom Toggle */}
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={useCustomMessage}
                  onCheckedChange={setUseCustomMessage}
                  id="custom-message"
                />
                <Label htmlFor="custom-message">Use custom message</Label>
              </div>

              {useCustomMessage ? (
                <div className="space-y-2">
                  <Label>Custom Message</Label>
                  <textarea
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    className="w-full min-h-[120px] p-3 border border-slate-200 rounded-lg text-sm"
                    placeholder="Enter your message... Use {{Recipient_Name}}, {{Hospital_Name}}, {{Date}} for placeholders"
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Select Template</Label>
                  <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a template" />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map(template => (
                        <SelectItem key={template.id} value={template.id}>
                          <div className="flex flex-col">
                            <span>{template.name}</span>
                            <span className="text-xs text-slate-500">{template.template_type}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {selectedTemplate && (
                    <div className="mt-2 p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
                      {templates.find(t => t.id === selectedTemplate)?.content}
                    </div>
                  )}
                </div>
              )}

              {/* Email Subject */}
              {selectedChannels.includes('email') && (
                <div className="space-y-2">
                  <Label>Email Subject</Label>
                  <Input
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="Enter email subject"
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Branding Options */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Image className="h-5 w-5" />
                Branding Options
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={includeBranding}
                  onCheckedChange={setIncludeBranding}
                  id="include-branding"
                />
                <Label htmlFor="include-branding">Include branding in messages</Label>
              </div>

              {includeBranding && (
                <div className="space-y-4 pt-2">
                  {/* Logo Upload */}
                  <div className="space-y-2">
                    <Label className="text-sm">Logo</Label>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    {logoPreview ? (
                      <div className="relative inline-block">
                        <img
                          src={logoPreview}
                          alt="Logo preview"
                          className="h-16 w-auto rounded border border-slate-200"
                        />
                        <button
                          type="button"
                          onClick={removeLogo}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={uploadingLogo}
                        className="w-full h-20 border-dashed flex flex-col gap-1"
                      >
                        {uploadingLogo ? (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                          <>
                            <Upload className="h-5 w-5 text-slate-400" />
                            <span className="text-xs text-slate-500">Click to upload logo</span>
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                  
                  {/* Tagline */}
                  <div className="space-y-2">
                    <Label className="text-sm">Tagline</Label>
                    <Input
                      value={tagline}
                      onChange={(e) => setTagline(e.target.value)}
                      placeholder="Your health, our priority"
                    />
                  </div>
                  
                  {/* Banner Upload */}
                  <div className="space-y-2">
                    <Label className="text-sm">Banner Image</Label>
                    <input
                      ref={bannerInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleBannerUpload}
                      className="hidden"
                    />
                    {bannerPreview ? (
                      <div className="relative">
                        <img
                          src={bannerPreview}
                          alt="Banner preview"
                          className="w-full h-24 object-cover rounded border border-slate-200"
                        />
                        <button
                          type="button"
                          onClick={removeBanner}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => bannerInputRef.current?.click()}
                        disabled={uploadingBanner}
                        className="w-full h-24 border-dashed flex flex-col gap-1"
                      >
                        {uploadingBanner ? (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                          <>
                            <Upload className="h-5 w-5 text-slate-400" />
                            <span className="text-xs text-slate-500">Click to upload banner image</span>
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              variant="outline"
              className="w-full"
              onClick={generatePreview}
              disabled={!getMessageContent()}
            >
              Preview Message
            </Button>
            
            <Button
              className="w-full bg-[#4A238F] hover:bg-[#3A1B72] text-white"
              onClick={handleSend}
              disabled={sending || selectedRecipients.length === 0}
            >
              {sending ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Send className="h-4 w-4" />
                  Send to {selectedRecipients.length} Recipients
                </span>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Message Preview</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {includeBranding && (
              <div className="text-center space-y-2 pb-4 border-b">
                {logoUrl && (
                  <img src={logoUrl} alt="Logo" className="h-16 mx-auto" />
                )}
                {tagline && (
                  <p className="text-sm text-slate-500 italic">{tagline}</p>
                )}
                {bannerUrl && (
                  <img src={bannerUrl} alt="Banner" className="w-full rounded-lg" />
                )}
              </div>
            )}
            
            <div className="p-4 bg-slate-50 rounded-lg whitespace-pre-wrap">
              {previewMessage}
            </div>
            
            <div className="flex gap-2">
              <Label className="text-sm text-slate-500">Channels:</Label>
              {selectedChannels.map(ch => (
                <Badge key={ch} variant="outline">{ch}</Badge>
              ))}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
